import {
  DELIVERY_PAYMENT_PATH,
  PROMOTIONS_PATH,
  TECHNICAL_PATH_PREFIXES,
  getCategoryByPath,
  getCategoryByQuery,
  getCategoryBySlug,
} from "./navigation";
import { buildProductDetailPath } from "../utils/productLinks";

export const SITE_NAME = "MyZooShop";
export const SITE_LOCALE = "uk_UA";
export const LOCAL_SITE_URL = "http://localhost:3000";
export const KNOWN_PRODUCTION_SITE_URL = "https://myzooshop.com.ua";
export const WWW_PRODUCTION_HOST = "www.myzooshop.com.ua";
export const CANONICAL_PRODUCTION_HOST = "myzooshop.com.ua";
export const DEFAULT_OG_IMAGE_PATH = "/Cat_in_box_logo_512.png";

export const normalizeCanonicalUrl = (url) => {
  const value = String(url || "").trim();
  if (!value) return "";

  try {
    const parsedUrl = new URL(value);
    if (parsedUrl.hostname === WWW_PRODUCTION_HOST) {
      parsedUrl.hostname = CANONICAL_PRODUCTION_HOST;
      parsedUrl.protocol = "https:";
    }
    return parsedUrl.toString().replace(/\/+$/, "");
  } catch {
    return value
      .replace(/^https:\/\/www\.myzooshop\.com\.ua/i, KNOWN_PRODUCTION_SITE_URL)
      .replace(/\/+$/, "");
  }
};

const normalizeSiteUrl = (url) => normalizeCanonicalUrl(url);

export const getSiteUrl = () => {
  const envUrl = process.env.REACT_APP_SITE_URL;
  if (envUrl) return normalizeSiteUrl(envUrl);

  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return LOCAL_SITE_URL;
    }
  }

  return KNOWN_PRODUCTION_SITE_URL;
};

export const SITE_URL = getSiteUrl();

export const absoluteUrl = (value = "/") => {
  if (/^https?:\/\//i.test(value)) return normalizeCanonicalUrl(value);
  const normalizedPath = value.startsWith("/") ? value : `/${value}`;
  return `${SITE_URL}${normalizedPath}`;
};

export const getDefaultOgImage = () => absoluteUrl(DEFAULT_OG_IMAGE_PATH);

export const normalizePathname = (pathname = "/") => {
  if (!pathname || pathname === "") return "/";
  const withoutTrailingSlash =
    pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  return withoutTrailingSlash || "/";
};

export const isTechnicalPath = (pathname) => {
  const normalized = normalizePathname(pathname);
  return TECHNICAL_PATH_PREFIXES.some(
    (prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`),
  );
};

const getSearchParams = (search = "") => new URLSearchParams(search);

const isProductDetailPath = (pathname = "") => {
  const normalized = normalizePathname(pathname);

  return (
    /^\/products\/[^/]+$/.test(normalized) ||
    /^\/category\/[^/]+\/[^/]+\/[^/]+\/[^/]+$/.test(normalized)
  );
};

const getBrandNameFromPath = (pathname = "") => {
  const normalized = normalizePathname(pathname);
  const match = normalized.match(/^\/brands\/([^/]+)$/);
  if (!match) return null;

  const decodedBrand = decodeURIComponent(match[1]).trim();
  if (!decodedBrand) return null;

  return decodedBrand
    .replace(/-/g, " ")
    .replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
};

const getPromotionCategoryFromPath = (pathname = "") => {
  const normalized = normalizePathname(pathname);
  const match = normalized.match(/^\/promotions\/category\/([^/]+)(?:\/([^/]+))?$/);
  if (!match) return null;

  const category = getCategoryBySlug(match[1]);
  if (!category) return null;

  return {
    category,
    kindSlug: match[2] || null,
    path: `${PROMOTIONS_PATH}/category/${category.slug}${
      match[2] ? `/${match[2]}` : ""
    }`,
  };
};

export const getCategoryKindFromPath = (pathname = "") => {
  const normalized = normalizePathname(pathname);
  const match = normalized.match(/^\/category\/([^/]+)\/([^/]+)$/);
  if (!match) return null;

  const category = getCategoryBySlug(match[1]);
  if (!category) return null;

  return {
    category,
    kindSlug: match[2],
    path: `/category/${category.slug}/${match[2]}`,
  };
};

export const getCategoryFromLocation = (pathname, search = "") => {
  const normalized = normalizePathname(pathname);
  const categoryKind = getCategoryKindFromPath(normalized);
  if (categoryKind) return categoryKind.category;

  const pathCategory = getCategoryByPath(normalized);
  if (pathCategory) return pathCategory;

  const categorySlugMatch = normalized.match(/^\/products\/category\/([^/]+)$/);
  if (categorySlugMatch) {
    return getCategoryBySlug(categorySlugMatch[1]);
  }

  const categoryQuery = getSearchParams(search).get("category");
  return categoryQuery ? getCategoryByQuery(categoryQuery) : null;
};

const hasOnlyIndexableDiscountParam = (search = "") => {
  const params = getSearchParams(search);
  const entries = [...params.entries()];
  if (entries.length !== 1) return false;
  const [key, value] = entries[0];
  return key === "discounted" && ["1", "true", "yes"].includes(String(value).toLowerCase());
};

export const getCanonicalPath = (pathname, search = "") => {
  const normalized = normalizePathname(pathname);
  if (isProductDetailPath(normalized)) return normalized;

  const promotionCategory = getPromotionCategoryFromPath(normalized);
  if (promotionCategory) return promotionCategory.path;

  const categoryKind = getCategoryKindFromPath(normalized);
  if (categoryKind) return categoryKind.path;

  const category = getCategoryFromLocation(normalized, search);

  if (category) return category.path;
  if (normalized === "/delivery-info" || normalized === "/delivery-payment") {
    return DELIVERY_PAYMENT_PATH;
  }
  if (normalized === "/sale") return PROMOTIONS_PATH;
  if (normalized === "/products" && hasOnlyIndexableDiscountParam(search)) {
    return PROMOTIONS_PATH;
  }
  return normalized;
};

const isUnknownCategoryListingPath = (pathname = "") => {
  const normalized = normalizePathname(pathname);
  const match =
    normalized.match(/^\/category\/([^/]+)(?:\/[^/]+)?$/) ||
    normalized.match(/^\/promotions\/category\/([^/]+)(?:\/[^/]+)?$/);

  return Boolean(match && !getCategoryBySlug(match[1]));
};

export const shouldNoIndex = (pathname, search = "") => {
  const normalized = normalizePathname(pathname);
  if (isTechnicalPath(normalized)) return true;
  if (isUnknownCategoryListingPath(normalized)) return true;

  const params = getSearchParams(search);
  if (!params.toString()) return false;

  if (
    isProductDetailPath(normalized)
  ) {
    return false;
  }

  return true;
};

const pageMeta = {
  "/": {
    title: "MyZooShop - онлайн-магазин товарів для тварин",
    description:
      "MyZooShop пропонує корм, аксесуари, засоби догляду та корисні товари для собак, котів, птахів, гризунів, риб і рептилій з доставкою по Україні.",
  },
  "/products": {
    title: "Каталог зоотоварів",
    description:
      "Переглядайте каталог MyZooShop: корм, ласощі, іграшки, аксесуари та засоби догляду для домашніх улюбленців.",
  },
  [PROMOTIONS_PATH]: {
    title: "Акційні товари для тварин — знижки на корм та аксесуари | MyZooShop",
    description:
      "Акційні товари для тварин у MyZooShop: знижки на корм, ласощі, іграшки, аксесуари та засоби догляду. Вигідні пропозиції з доставкою по Україні.",
  },
  [DELIVERY_PAYMENT_PATH]: {
    title: "Оплата, доставка, повернення та обмін",
    description:
      "Оплата, доставка Новою поштою, актуальні умови безкоштовної доставки, повернення та обмін у MyZooShop.",
  },
  "/bonus-program": {
    title: "Бонусна програма",
    description:
      "Бонусна програма MyZooShop: актуальні ставки нарахування, використання бонусів у гривнях, строк дії та правила повернення.",
  },
  "/delivery-payment": {
    title: "Оплата, доставка, повернення та обмін",
    description:
      "Оплата, доставка Новою поштою, актуальні умови безкоштовної доставки, повернення та обмін у MyZooShop.",
  },
  "/delivery-info": {
    title: "Оплата, доставка, повернення та обмін",
    description:
      "Оплата, доставка Новою поштою, актуальні умови безкоштовної доставки, повернення та обмін у MyZooShop.",
  },
  "/cart": {
    title: "Кошик",
    description:
      "Перегляд товарів у кошику MyZooShop перед оформленням замовлення.",
  },
  "/checkout": {
    title: "Оформлення замовлення",
    description: "Контактні дані, доставка та оплата замовлення у MyZooShop.",
  },
  "/order-summary": {
    title: "Перегляд замовлення",
    description: "Підтвердження складу, доставки та оплати замовлення.",
  },
  "/order-confirmation": {
    title: "Замовлення створено",
    description: "Підтвердження створення замовлення у MyZooShop.",
  },
  "/payment-requisites": {
    title: "Реквізити для оплати",
    description: "Реквізити для оплати сформованого замовлення.",
  },
  "/login": {
    title: "Вхід до системи",
    description: "Службова сторінка входу до адміністративної частини.",
  },
  "/reset-password": {
    title: "Відновлення паролю",
    description: "Службова сторінка встановлення нового паролю.",
  },
};

export const formatTitle = (title) =>
  title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;

export const getRouteSeo = ({ pathname, search = "", categoryName = null }) => {
  const normalized = normalizePathname(pathname);
  const categoryKind = getCategoryKindFromPath(normalized);
  const promotionCategory = getPromotionCategoryFromPath(normalized);
  const category = getCategoryFromLocation(normalized, search);
  const brandName = getBrandNameFromPath(normalized);
  const noindex = shouldNoIndex(normalized, search);
  const canonicalPath = getCanonicalPath(normalized, search);

  if (promotionCategory) {
    const categoryLabel = promotionCategory.category.menuLabel.toLowerCase();
    const titleSuffix = promotionCategory.kindSlug
      ? " - акційні пропозиції за видом товарів"
      : " - знижки на корм та аксесуари";

    return {
      title: formatTitle(`Акційні ${categoryLabel}${titleSuffix}`),
      description: `Купуйте акційні ${categoryLabel} у MyZooShop: корм, ласощі, іграшки, засоби догляду та аксесуари зі знижками. Доставка по Україні.`,
      canonicalUrl: absoluteUrl(canonicalPath),
      robots: noindex ? "noindex, nofollow" : "index, follow",
      ogType: "website",
      image: getDefaultOgImage(),
    };
  }

  if (categoryKind) {
    const categoryLabel = categoryKind.category.menuLabel.toLowerCase();

    return {
      title: formatTitle(`${categoryKind.category.menuLabel} - вид товарів`),
      description: `Добірка товарів у розділі ${categoryLabel} за обраним видом. Переглядайте актуальні товари MyZooShop з доставкою по Україні.`,
      canonicalUrl: absoluteUrl(canonicalPath),
      robots: noindex ? "noindex, nofollow" : "index, follow",
      ogType: "website",
      image: getDefaultOgImage(),
    };
  }

  if (category) {
    return {
      title: category.seoTitle || formatTitle(category.menuLabel),
      description:
        category.metaDescription ||
        `${category.description} Замовляйте у MyZooShop з доставкою по Україні.`,
      canonicalUrl: absoluteUrl(category.path),
      robots: noindex ? "noindex, nofollow" : "index, follow",
      ogType: "website",
      image: getDefaultOgImage(),
    };
  }

  if (brandName) {
    return {
      title: formatTitle(`Товари бренду ${brandName}`),
      description: `Переглядайте товари бренду ${brandName} у каталозі MyZooShop. Актуальні ціни, наявність і доставка по Україні.`,
      canonicalUrl: absoluteUrl(canonicalPath),
      robots: noindex ? "noindex, nofollow" : "index, follow",
      ogType: "website",
      image: getDefaultOgImage(),
    };
  }

  if (categoryName && normalized.startsWith("/products")) {
    const titlePart = categoryName.replace(/^Для\s+/i, "Товари для ");
    return {
      title: formatTitle(titlePart),
      description: `Добірка товарів: ${categoryName}. Фільтруйте каталог MyZooShop за видом, виробником, ціною та потребами вашого улюбленця.`,
      canonicalUrl: absoluteUrl(canonicalPath),
      robots: noindex ? "noindex, nofollow" : "index, follow",
      ogType: "website",
      image: getDefaultOgImage(),
    };
  }

  const meta = pageMeta[canonicalPath] || pageMeta[normalized] || pageMeta["/"];

  return {
    title: formatTitle(meta.title),
    description: meta.description,
    canonicalUrl: absoluteUrl(canonicalPath),
    robots: noindex ? "noindex, nofollow" : "index, follow",
    ogType: isProductDetailPath(normalized) ? "product" : "website",
    image: getDefaultOgImage(),
  };
};

export const buildOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: SITE_NAME,
  url: SITE_URL,
  logo: absoluteUrl("/Cat_in_box_logo_512.png"),
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: "+380671234567",
      contactType: "customer support",
      areaServed: "UA",
      availableLanguage: ["uk"],
    },
  ],
});

export const buildWebsiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  name: SITE_NAME,
  url: SITE_URL,
  publisher: { "@id": `${SITE_URL}/#organization` },
  inLanguage: "uk-UA",
});

export const buildBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: absoluteUrl(item.path),
  })),
});

export const buildProductSchema = ({ product, variant, url, image }) => {
  const price = Number(variant?.discountPrice || variant?.price || 0);
  const availability =
    Number(variant?.totalAvailable ?? variant?.totalQuantity ?? 0) > 0
      ? "https://schema.org/InStock"
      : "https://schema.org/OutOfStock";

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": `${url}#product`,
    name: product.name,
    image: [image],
    description: String(product.description || "").slice(0, 500),
    sku: variant?.productCode || product.productCode,
    brand: {
      "@type": "Brand",
      name: product.manufacturer || SITE_NAME,
    },
    category: Array.isArray(product.category)
      ? product.category.join(", ")
      : product.category,
    offers: {
      "@type": "Offer",
      url,
      priceCurrency: "UAH",
      price,
      availability,
      itemCondition: "https://schema.org/NewCondition",
    },
  };
};

export const buildItemListSchema = ({ name, items }) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name,
  itemListElement: items.map((product, index) => ({
    "@type": "ListItem",
    position: index + 1,
    url: absoluteUrl(buildProductDetailPath(product) || `/products/${product.slug}`),
    name: product.name,
  })),
});
